import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAppSelector } from '../../store'
import { userApi } from '../../api/userApi'
import { User } from '../../types'
import {
  Briefcase, Edit, Loader2, Mail, Phone,
  Download, FileText, Sparkles, Link as LinkIcon,
} from 'lucide-react'
import { toast } from 'react-hot-toast'

export default function ProfilePage() {
  const {} = useAppSelector((s) => s.auth)
  const [profile, setProfile] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    userApi
      .getMe()
      .then(({ data }) => setProfile(data.data!))
      .catch(() => toast.error('Failed to load profile'))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 size={30} className="animate-spin text-primary-600" />
      </div>
    )
  }

  if (!profile) return null

  const avatarInitial = (profile.profile?.displayName || profile.email)[0].toUpperCase()

  return (
    <div className="max-w-2xl mx-auto flex flex-col gap-3">

      {/* ── Header card ── */}
      <div
        className="bg-white rounded-lg overflow-hidden"
        style={{ boxShadow: '0 0 0 1px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.05)' }}
      >
        {/* Cover banner */}
        <div
          className="h-36"
          style={{
            background: 'linear-gradient(135deg, #0A66C2 0%, #004182 50%, #001A3B 100%)',
          }}
        />

        {/* Avatar + actions row */}
        <div className="px-5 pb-5">
          <div className="flex items-end justify-between -mt-10 mb-3">
            {/* Avatar */}
            <div className="w-24 h-24 rounded-full border-4 border-white bg-primary-100 flex items-center justify-center overflow-hidden shadow-lg flex-shrink-0">
              {profile.profile?.profilePicUrl ? (
                <img
                  src={profile.profile.profilePicUrl}
                  alt={profile.profile.displayName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-3xl font-bold text-primary-600">{avatarInitial}</span>
              )}
            </div>

            {/* Edit button */}
            <Link
              to="/profile/edit"
              className="flex items-center gap-1.5 px-4 py-1.5 border border-primary-600 text-primary-600 text-sm font-semibold rounded-full hover:bg-primary-50 transition-colors no-underline"
            >
              <Edit size={14} /> Edit profile
            </Link>
          </div>

          {/* Name & headline */}
          <h1 className="text-xl font-bold text-gray-900 leading-tight">
            {profile.profile?.displayName || 'No name set'}
          </h1>

          {profile.profile?.bio && (
            <p className="text-sm text-gray-600 mt-1 leading-relaxed max-w-md">
              {profile.profile.bio}
            </p>
          )}

          {/* Meta row */}
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-sm text-gray-500">
            {profile.profile?.experienceYears !== undefined && (
              <span className="flex items-center gap-1.5">
                <Briefcase size={14} className="text-gray-400" />
                {profile.profile.experienceYears} yrs experience
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Mail size={14} className="text-gray-400" />
              {profile.email}
            </span>
            {profile.phone && (
              <span className="flex items-center gap-1.5">
                <Phone size={14} className="text-gray-400" />
                {profile.phone}
              </span>
            )}
            {(profile.profile as any)?.linkedinUrl && (
              <a
                href={(profile.profile as any).linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-primary-600 hover:underline no-underline"
              >
                <LinkIcon size={14} /> LinkedIn
              </a>
            )}
          </div>

          {/* Role badges */}
          <div className="flex flex-wrap gap-2 mt-4">
            {profile.roles.map((role) => (
              <span key={role} className="badge badge-blue">{role}</span>
            ))}
            {profile.activeRole && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 ring-1 ring-primary-200">
                Active: {profile.activeRole}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Skills card ── */}
      {profile.profile?.skills &&
        Array.isArray(profile.profile.skills) &&
        profile.profile.skills.length > 0 && (
          <div
            className="bg-white rounded-lg p-5"
            style={{ boxShadow: '0 0 0 1px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.05)' }}
          >
            <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2 text-base">
              <Sparkles size={16} className="text-primary-600" /> Skills
            </h2>
            <div className="flex flex-wrap gap-2">
              {profile.profile.skills.map((skill) => (
                <span
                  key={skill}
                  className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-[#EEF3FB] text-primary-700 ring-1 ring-primary-100 hover:bg-primary-100 transition-colors cursor-default"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

      {/* ── Experience card ── */}
      {profile.profile?.experienceYears !== undefined && (
        <div
          className="bg-white rounded-lg p-5"
          style={{ boxShadow: '0 0 0 1px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.05)' }}
        >
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2 text-base">
            <Briefcase size={16} className="text-primary-600" /> Experience
          </h2>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <Briefcase size={16} className="text-primary-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{profile.activeRole}</p>
              <p className="text-xs text-gray-500">
                {profile.profile.experienceYears} year{profile.profile.experienceYears !== 1 ? 's' : ''} of experience
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Resume card ── */}
      {profile.profile?.resumeUrl && (
        <div
          className="bg-white rounded-lg p-5"
          style={{ boxShadow: '0 0 0 1px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.05)' }}
        >
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2 text-base">
            <FileText size={16} className="text-primary-600" /> Resume
          </h2>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
            <div className="w-10 h-10 rounded bg-primary-100 flex items-center justify-center flex-shrink-0">
              <FileText size={18} className="text-primary-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">Resume.pdf</p>
              <p className="text-xs text-gray-500">PDF document</p>
            </div>
            <a
              href={profile.profile.resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded-full text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors no-underline flex-shrink-0"
            >
              <Download size={13} /> View
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
